import { type CompanyAccountFeature } from '~/modules/clients/companyAccounts/utils';

type UserFeatureFlagConfig =
  | {
      readonly default: boolean;
      readonly isAlsoGrantedByAccount?: true;
      readonly isDeprecated?: true;
      readonly kind: 'boolean';
    }
  | { readonly kind: 'assistant' | 'readPath' };

/**
 * Every feature flag stored in a user's settings, with how it is edited:
 * - `boolean`: a plain flag; a missing key means `default`. With `isAlsoGrantedByAccount`,
 *   the company account's flag of the same name grants it too, whatever the user's value.
 * - `assistant`: the personal Assistant override. `true` enables it, `false` or `null`
 *   disables it, and a missing key inherits the company account. A stored override never
 *   inherits again.
 * - `readPath`: switches one delivery-note read path from Couchbase (`false`) to
 *   Postgres (`true`); `null` or a missing key inherits the company account.
 */
export const USER_FEATURE_FLAGS = {
  accessPermittedSites: { default: true, kind: 'boolean' },
  agentChat: { kind: 'assistant' },
  analyticsUser: { default: false, kind: 'boolean' },
  concreteDiary: { default: false, isDeprecated: true, kind: 'boolean' },
  concreteEditingEnabled: { default: false, kind: 'boolean' },
  dlnListV2: { default: false, kind: 'boolean' },
  lineItemDetailsEditingDisabled: { default: false, kind: 'boolean' },
  manageCallOffs: { default: false, kind: 'boolean' },
  manageContracts: { default: false, kind: 'boolean' },
  manualInvoiceCheckingPipeline: {
    default: false,
    isAlsoGrantedByAccount: true,
    kind: 'boolean',
  },
  ocrEnabled: { default: false, kind: 'boolean' },
  pgDlnAnalytics: { kind: 'readPath' },
  pgDlnAttachments: { kind: 'readPath' },
  pgDlnBulk: { kind: 'readPath' },
  pgDlnChains: { kind: 'readPath' },
  pgDlnDetail: { kind: 'readPath' },
  pgDlnList: { kind: 'readPath' },
  pgDlnSearch: { kind: 'readPath' },
  pgDlnTransformed: { kind: 'readPath' },
  pgDlnVadmin: { kind: 'readPath' },
  recipientSetArrived: { default: false, kind: 'boolean' },
  showOrderAtPreview: { default: false, isDeprecated: true, kind: 'boolean' },
  userCannotSetAccepted: { default: false, kind: 'boolean' },
  userCannotSetDeclined: { default: false, kind: 'boolean' },
  userCannotSetDelivered: { default: false, kind: 'boolean' },
  userMetrics: { default: false, isDeprecated: true, kind: 'boolean' },
} as const satisfies Record<string, UserFeatureFlagConfig>;

type UserFeatureFlagKey = keyof typeof USER_FEATURE_FLAGS;

type UserFeatureFlagKeyOfKind<TKind extends UserFeatureFlagConfig['kind']> = {
  [
    TFlag in UserFeatureFlagKey
  ]: (typeof USER_FEATURE_FLAGS)[TFlag]['kind'] extends TKind ? TFlag : never;
}[UserFeatureFlagKey];

export const USER_FEATURE_FLAG_KEYS = Object.keys(
  USER_FEATURE_FLAGS,
) as UserFeatureFlagKey[];

function flagsOfKind<TKind extends UserFeatureFlagConfig['kind']>(kind: TKind) {
  return USER_FEATURE_FLAG_KEYS.filter(
    (flag): flag is UserFeatureFlagKeyOfKind<TKind> =>
      USER_FEATURE_FLAGS[flag].kind === kind,
  );
}

export const PLAIN_USER_FEATURE_FLAGS = flagsOfKind('boolean');

/**
 * The read-path overrides. The company account sets the default of each path.
 */
export const PG_DLN_READ_PATH_FLAGS = flagsOfKind(
  'readPath',
) satisfies readonly CompanyAccountFeature[];

/**
 * The `detail.code` values of an error response of the single sign-on link endpoints.
 */
export const USER_IDENTITY_PROVIDER_LINK_ERROR_CODES = [
  'login_not_found',
  'identity_provider_link_not_found',
  'login_service_read_only',
  'login_service_unavailable',
] as const;
